import type { FmElement } from '../elements/elements.types';
import type { FmSection } from './section.types';

/**
 * The editorial pace vocabulary is opt-in per archetype (owner ruling
 * 2026-10-13). A story surface may use it sparingly; a scanning surface takes
 * at most a stat band and a pace; tool surfaces (map, analysis, risk, forms,
 * operational stages) take none and stay dense. Each capability is named by
 * the option that turns it on, so a section's use is read off its descriptor.
 */
export const FM_PACE_CAPABILITIES = [
  'displayTier',
  'statBand',
  'visualBand',
  'imageBand',
  'editorialSplit',
  'pace',
] as const;
export type FmPaceCapability = (typeof FM_PACE_CAPABILITIES)[number];

export const FM_ARCHETYPE_PACE_CAPABILITIES = {
  FmReportPageTyped: FM_PACE_CAPABILITIES,
  FmProductPage: FM_PACE_CAPABILITIES,
  FmDashboardOverview: ['statBand', 'pace'],
  FmOperationalWorkspaceRoot: [],
  FmMapWorkspaceRoot: [],
  FmAnalysisWorkspaceRoot: [],
  FmRiskWorkspaceRoot: [],
  FmFormFlowPage: [],
} as const satisfies Record<string, readonly FmPaceCapability[]>;
export type FmPaceArchetype = keyof typeof FM_ARCHETYPE_PACE_CAPABILITIES;

function sectionElements(section: FmSection): readonly FmElement[] {
  const slots = 'slots' in section && section.slots ? Object.values(section.slots) : [];
  const panels =
    'panels' in section && section.panels
      ? section.panels
      : 'slides' in section && section.slides
        ? section.slides
        : [];
  return [
    ...slots.flatMap((elements: readonly FmElement[] | undefined) => elements ?? []),
    ...panels.flatMap((panel: { elements: readonly FmElement[] }) => panel.elements),
  ];
}

/** The pace capabilities a section turns on, in vocabulary order. */
export function sectionPaceCapabilities(section: FmSection): FmPaceCapability[] {
  const elements = sectionElements(section);
  const used = new Set<FmPaceCapability>();
  if (elements.some((element) => element.element === 'copy' && element.scale === 'display')) {
    used.add('displayTier');
  }
  if (elements.some((element) => element.element === 'keyFigures' && element.scale === 'display')) {
    used.add('statBand');
  }
  if (section.arrangement === 'split' && section.width === 'full') used.add('visualBand');
  if (section.arrangement === 'band' && (section.scrim || section.imageFit)) used.add('imageBand');
  if (section.arrangement === 'prose' && section.headline === 'beside') used.add('editorialSplit');
  if (section.density && section.density !== 'comfortable') used.add('pace');
  return FM_PACE_CAPABILITIES.filter((capability) => used.has(capability));
}

/** Refuses a section that uses a pace capability its archetype does not take. */
export function assertArchetypePaceCapabilities(
  archetype: FmPaceArchetype,
  section: FmSection,
  path: string,
): void {
  const allowed: readonly FmPaceCapability[] = FM_ARCHETYPE_PACE_CAPABILITIES[archetype];
  const refused = sectionPaceCapabilities(section).filter(
    (capability) => !allowed.includes(capability),
  );
  if (refused.length) {
    throw new Error(
      `Invalid descriptor: ${path} uses ${refused.join(', ')}, which ${archetype} does not take; ` +
        `it takes ${allowed.length ? allowed.join(', ') : 'none of the pace vocabulary'}.`,
    );
  }
}
